import React, { useState } from 'react';
import {
  Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  Typography, Box, Divider, Button, IconButton
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Lock as LockIcon, Edit as EditIcon, Warning as WarningIcon } from '@mui/icons-material';
import ESocialReferenceAutocomplete from './ESocialReferenceAutocomplete';
import type { S2220Metadata } from '../../../../../types/esocial-forms';

interface Props {
  metadata: Partial<S2220Metadata> | null;
  onChange: (metadata: Partial<S2220Metadata>) => void;
  gaps?: string[];
  overrideEnabled?: boolean;
}

const UF_OPTIONS = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
  'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'
];

const S2220Form: React.FC<Props> = ({ metadata, onChange, gaps = [], overrideEnabled = false }) => {
  const [isOverride, setIsOverride] = useState(false);
  const exMedOcup = metadata?.exMedOcup ?? {
    tpExameOcup: 0,
    aso: { dtAso: '', resAso: 1 },
    medico: { nmMed: '', nrCRM: '', ufCRM: '' }
  };
  const aso = exMedOcup.aso;
  const exames = aso.exames ?? [];

  const update = (path: string, value: unknown) => {
    const parts = path.split('.');
    const result = JSON.parse(JSON.stringify(metadata ?? {})) as Record<string, unknown>;
    if (!result['exMedOcup']) {
      result['exMedOcup'] = JSON.parse(JSON.stringify(exMedOcup));
    }
    let obj = result['exMedOcup'] as Record<string, unknown>;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!obj[parts[i]!]) obj[parts[i]!] = {};
      obj = obj[parts[i]!] as Record<string, unknown>;
    }
    obj[parts[parts.length - 1]!] = value;
    onChange(result as Partial<S2220Metadata>);
  };

  const addExame = () => {
    const newExames = [...exames, { dtExm: '', procRealizado: '', ordExame: 1 }];
    update('aso.exames', newExames);
  };

  const removeExame = (index: number) => {
    update('aso.exames', exames.filter((_, i) => i !== index));
  };

  const updateExame = (index: number, field: string, value: unknown) => {
    const newExames = [...exames];
    newExames[index] = { ...newExames[index]!, [field]: value };
    update('aso.exames', newExames);
  };

  return (
    <Box>
      {/* Review mode header */}
      {gaps.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" fontSize="small" />
          <Typography variant="body2" color="warning.main">
            {gaps.length} campo(s) pendente(s) de preenchimento
          </Typography>
          {overrideEnabled && (
            <Button
              size="small"
              variant={isOverride ? 'contained' : 'outlined'}
              startIcon={isOverride ? <LockIcon /> : <EditIcon />}
              onClick={() => setIsOverride(!isOverride)}
              sx={{ ml: 'auto' }}
            >
              {isOverride ? 'Bloquear campos' : 'Editar todos os campos'}
            </Button>
          )}
        </Box>
      )}

      {/* Exame Ocupacional */}
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Exame Ocupacional</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth>
            <InputLabel>Tipo de Exame *</InputLabel>
            <Select value={exMedOcup.tpExameOcup ?? 0} onChange={(e) => update('tpExameOcup', e.target.value)} label="Tipo de Exame *">
              <MenuItem value={0}>Admissional</MenuItem>
              <MenuItem value={1}>Periodico</MenuItem>
              <MenuItem value={2}>Retorno ao Trabalho</MenuItem>
              <MenuItem value={3}>Mudanca de Risco</MenuItem>
              <MenuItem value={4}>Demissional</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* ASO */}
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>ASO</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth type="date" label="Data do ASO *" value={aso.dtAso || ''} onChange={(e) => update('aso.dtAso', e.target.value)} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth>
            <InputLabel>Resultado *</InputLabel>
            <Select value={aso.resAso ?? 1} onChange={(e) => update('aso.resAso', e.target.value)} label="Resultado *">
              <MenuItem value={1}>Apto</MenuItem>
              <MenuItem value={2}>Inapto</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Exames Complementares */}
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Exames Complementares</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addExame}>Adicionar</Button>
      </Box>
      {exames.map((exame, idx) => (
        <Box key={idx} sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Exame {idx + 1}</Typography>
            <IconButton size="small" onClick={() => removeExame(idx)}><DeleteIcon fontSize="small" /></IconButton>
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth type="date" label="Data *" value={exame.dtExm || ''} onChange={(e) => updateExame(idx, 'dtExm', e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <ESocialReferenceAutocomplete tableNumber={27} value={exame.procRealizado || ''} onChange={(code) => updateExame(idx, 'procRealizado', code)} label="Procedimento (Tab. 27) *" required size="small" />
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Ordem</InputLabel>
                <Select value={exame.ordExame ?? 1} onChange={(e) => updateExame(idx, 'ordExame', e.target.value)} label="Ordem">
                  <MenuItem value={1}>Referencial</MenuItem>
                  <MenuItem value={2}>Sequencial</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Resultado</InputLabel>
                <Select value={exame.indResult ?? ''} onChange={(e) => updateExame(idx, 'indResult', e.target.value || undefined)} label="Resultado">
                  <MenuItem value="">-</MenuItem>
                  <MenuItem value={1}>Normal</MenuItem>
                  <MenuItem value={2}>Alterado</MenuItem>
                  <MenuItem value={3}>Estavel</MenuItem>
                  <MenuItem value={4}>Agravamento</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      ))}

      {/* Medico Examinador */}
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Medico Examinador</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 5 }}>
          <TextField fullWidth label="Nome do Medico *" value={exMedOcup.medico?.nmMed || ''} onChange={(e) => update('medico.nmMed', e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth label="CRM *" value={exMedOcup.medico?.nrCRM || ''} onChange={(e) => update('medico.nrCRM', e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <FormControl fullWidth>
            <InputLabel>UF CRM *</InputLabel>
            <Select value={exMedOcup.medico?.ufCRM || ''} onChange={(e) => update('medico.ufCRM', e.target.value)} label="UF CRM *">
              {UF_OPTIONS.map(uf => <MenuItem key={uf} value={uf}>{uf}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Coordenador PCMSO */}
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Coordenador PCMSO (opcional)</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 5 }}>
          <TextField fullWidth label="Nome do Responsavel" value={exMedOcup.respMonit?.nmResp || ''} onChange={(e) => update('respMonit.nmResp', e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth label="CRM" value={exMedOcup.respMonit?.nrCRM || ''} onChange={(e) => update('respMonit.nrCRM', e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <FormControl fullWidth>
            <InputLabel>UF CRM</InputLabel>
            <Select value={exMedOcup.respMonit?.ufCRM || ''} onChange={(e) => update('respMonit.ufCRM', e.target.value)} label="UF CRM">
              <MenuItem value="">-</MenuItem>
              {UF_OPTIONS.map(uf => <MenuItem key={uf} value={uf}>{uf}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
};

export default S2220Form;
